import { range as d3Range, zip } from 'd3-array';
import { forceCollide, forceSimulation, forceX } from 'd3-force';
import type { ScaleContinuousNumeric } from 'd3-scale';
import * as React from 'react';
import { AxisZoomCapture } from './AxisZoomCapture';
import type { strokeDashTypes } from 'react-financial-charts';
import {
  first,
  GenericChartComponent,
  getAxisCanvas,
  getStrokeDasharrayCanvas,
  isDefined,
  last,
  noop,
  ChartCanvasContext,
} from 'react-financial-charts';
import type { MoreProps } from '@/types/commontypes';
// eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
const ChartComponent = GenericChartComponent as unknown as React.ComponentType<Record<string, unknown>>;
interface AxisProps {
  readonly axisZoomCallback?: (domain: number[]) => void;
  readonly bg: {
    h: number;
    x: number;
    w: number;
    y: number;
  };
  readonly className?: string;
  readonly domainClassName?: string;
  readonly edgeClip: boolean;
  readonly fontFamily?: string;
  readonly fontSize?: number;
  readonly fontWeight?: number;
  readonly getMouseDelta: (startXY: [number, number], mouseXY: [number, number]) => number;
  readonly getScale: (moreProps: MoreProps) => ScaleContinuousNumeric<number, number>;
  readonly innerTickSize?: number;
  readonly inverted?: boolean;
  readonly onContextMenu?: (e: React.MouseEvent, mousePosition: [number, number]) => void;
  readonly onDoubleClick?: (e: React.MouseEvent, mousePosition: [number, number]) => void;
  readonly orient?: 'top' | 'left' | 'right' | 'bottom';
  readonly outerTickSize: number;
  readonly range: number[];
  readonly showDomain?: boolean;
  readonly showGridLines?: boolean;
  readonly gridLinesStrokeStyle?: string;
  readonly gridLinesStrokeDasharray?: strokeDashTypes;
  readonly showTicks?: boolean;
  readonly showTickLabel?: boolean;
  readonly strokeStyle: string;
  readonly strokeWidth: number;
  readonly tickFormat?: (data: number | Date) => string;
  readonly tickPadding?: number;
  readonly tickSize?: number;
  readonly ticks?: number;
  readonly tickLabelFill?: string;
  readonly tickStrokeStyle?: string;
  readonly tickStrokeWidth?: number;
  readonly tickStrokeDasharray?: strokeDashTypes;
  readonly tickValues?: number[] | ((domain: number[]) => number[]);
  readonly tickInterval?: number;
  readonly tickIntervalFunction?: (min: number, max: number, tickInterval: number) => number[];
  readonly transform: number[];
  readonly zoomEnabled?: boolean;
  readonly zoomCursorClassName?: string;

  readonly onAxisPositionChange?: (newAxisY: number) => void;
}

interface Tick {
  value: number;
  x1: number;
  y1: number;
  x2: number;
  y2: number;
  labelX: number;
  labelY: number;
}

interface AxisState {
  initialDragY?: number;
  initialHeight?: number;
  newXYValue?: { mouseXY: [number, number] } | null;
  dragging: boolean;
  isHovered: boolean;
}

interface ForceNode {
  id: number;
  value: number;
  fy: number;
  origX: number;
  x?: number;
}

export class Axis extends React.Component<AxisProps, AxisState> {
  public static defaultProps = {
    edgeClip: false,
    zoomEnabled: false,
    zoomCursorClassName: '',
    onAxisComplete: noop,
    // newXYValue: noop
  };

  public static contextType = ChartCanvasContext;
  declare context: React.ContextType<typeof ChartCanvasContext>;

  constructor(props: AxisProps) {
    super(props);
    this.state = {
      dragging: false,
      isHovered: false,
    };
  }
  private selected: boolean = false;
  private readonly chartRef = React.createRef<GenericChartComponent>();

  public render() {
    const {
      bg,
      axisZoomCallback,
      className,
      zoomCursorClassName,
      zoomEnabled,
      getScale,
      inverted,
      transform,
      getMouseDelta,
      edgeClip,
      onContextMenu,
      onDoubleClick,
      orient,
    } = this.props;
    const isXAxis = orient === 'bottom' || orient === 'top';
    const zoomCapture = zoomEnabled ? (
      <AxisZoomCapture
        bg={bg}
        getScale={getScale}
        getMoreProps={this.getMoreProps}
        getMouseDelta={getMouseDelta}
        axisZoomCallback={axisZoomCallback}
        className={className}
        zoomCursorClassName={zoomCursorClassName}
        inverted={inverted}
        onContextMenu={onContextMenu}
        onDoubleClick={onDoubleClick}
      />
    ) : null;

    return (
      <g transform={`translate(${transform[0]}, ${transform[1]})`}>
        {zoomCapture}
        <ChartComponent
          ref={this.chartRef}
          canvasToDraw={getAxisCanvas}
          isHover={isXAxis ? this.isHover : undefined}
          clip={false}
          selected={this.selected}
          edgeClip={edgeClip}
          interactiveCursorClass={'row-resize-arrow-cursor'}
          canvasDraw={this.drawOnCanvas}
          drawOn={['pan']}
          onHover={isXAxis ? this.handleHover : undefined}
          onUnHover={isXAxis ? this.handleUnHover : undefined}
          onDragStart={isXAxis ? this.handleDragStart : undefined}
          onDrag={isXAxis ? this.handleDrag : undefined}
          onDragComplete={isXAxis ? this.handleDragComplete : undefined}
          disablePan={false}
        />
      </g>
    );
  }

  private readonly getMoreProps = () => {
    return this.chartRef.current!.getMoreProps();
  };

  private readonly drawOnCanvas = (ctx: CanvasRenderingContext2D, moreProps: MoreProps) => {
    const { showDomain, showGridLines, showTickLabel, showTicks, transform, range, getScale, tickLabelFill, orient } =
      this.props;

    // context에서 margin 가져오기
    const { margin } = this.context;

    const isXAxis = orient === 'bottom' || orient === 'top';
    ctx.save();
    ctx.translate(transform[0], transform[1]);

    const scale = getScale(moreProps);
    const tickProps = tickHelper(this.props, scale);
    if (showTicks) {
      drawTicks(ctx, tickProps);
    }

    if (showGridLines) {
      tickProps.ticks.forEach(tick => {
        drawGridLine(ctx, tick, tickProps, moreProps, margin);
      });
    }

    if (showTickLabel) {
      const { fontFamily, fontSize, fontWeight, textAnchor } = tickProps;

      ctx.font = `${fontWeight} ${fontSize}px ${fontFamily}`;
      if (tickLabelFill !== undefined) {
        ctx.fillStyle = tickLabelFill;
      }
      // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
      ctx.textAlign = textAnchor === 'middle' ? 'center' : (textAnchor as CanvasTextAlign);
      tickProps.ticks.forEach((tick: Tick) => {
        drawEachTickLabel(ctx, tick, tickProps);
      });
    }

    if (showDomain) {
      if (isXAxis) {
        const { dragging, isHovered } = this.state;
        drawAxisLine(ctx, this.props, range, dragging, isHovered, this.selected);
      } else {
        drawAxisLine(ctx, this.props, range);
      }
    }

    ctx.restore();
  };

  private readonly isHover = (moreProps: MoreProps, _e: unknown) => {
    const { mouseXY, chartConfig } = moreProps;
    const [_x, y] = mouseXY;

    const chartTop = chartConfig.origin[1];
    const axisY = chartTop + this.props.transform[1];

    const hoverToleranceAbove = 2;
    const hoverToleranceBelow = 2;

    const isWithinTolerance = y >= axisY - hoverToleranceAbove && y <= axisY + hoverToleranceBelow;

    this.selected = isWithinTolerance;
    return isWithinTolerance;
  };

  private readonly handleDrag = (_e: unknown, moreProps: MoreProps) => {
    const { mouseXY } = moreProps;
    const { initialDragY, initialHeight } = this.state;
    const { onAxisPositionChange } = this.props;

    if (initialDragY === undefined || initialHeight === undefined) {
      return; // drag hasn't started properly
    }
    // Calculate vertical delta
    const deltaY = mouseXY[1] - initialDragY;

    // Calculate new height by adding deltaY
    // Dragging mouse down (deltaY > 0) increases height
    // Dragging mouse up (deltaY < 0) decreases height
    let newHeight = initialHeight + deltaY;

    // Optional: clamp height to minimum, e.g. 100
    newHeight = Math.max(newHeight, 100);

    if (onAxisPositionChange) {
      onAxisPositionChange(newHeight);
    }
    this.setState({
      newXYValue: { mouseXY },
    });
  };

  private readonly handleDragStart = (_e: unknown, moreProps: MoreProps) => {
    const {
      mouseXY,
      chartConfig: { height },
    } = moreProps;

    // Save initial drag Y and height at drag start
    this.setState({
      initialDragY: mouseXY[1],
      initialHeight: height,
      dragging: true,
    });
  };

  private readonly handleDragComplete = (_e: unknown, _moreProps: MoreProps) => {
    if (this.state.newXYValue) {
      this.selected = false;
      this.setState({
        newXYValue: null,
        dragging: false,
        isHovered: false,
      });
    }
  };

  private readonly handleHover = () => {
    if (!this.state.isHovered) {
      this.setState({ isHovered: true });
    }
  };

  private readonly handleUnHover = () => {
    if (this.state.isHovered) {
      this.setState({ isHovered: false });
    }
  };
}

const drawAxisLine = (
  ctx: CanvasRenderingContext2D,
  props: AxisProps,
  range: number[],
  isDragging?: boolean,
  isHovered?: boolean,
  selected?: boolean,
) => {
  const { orient, outerTickSize, strokeStyle, strokeWidth } = props;

  const sign = orient === 'top' || orient === 'left' ? -1 : 1;
  const xAxis = orient === 'bottom' || orient === 'top';

  // Draw the base black line always
  ctx.lineWidth = strokeWidth;
  ctx.strokeStyle = strokeStyle;

  const firstPoint = first(range);
  const lastPoint = last(range);
  const tickSize = sign * outerTickSize;

  ctx.beginPath();
  if (xAxis) {
    ctx.moveTo(firstPoint, tickSize);
    ctx.lineTo(firstPoint, 0);
    ctx.lineTo(lastPoint, 0);
    ctx.lineTo(lastPoint, tickSize);
  } else {
    ctx.moveTo(tickSize, firstPoint);
    ctx.lineTo(0, firstPoint);
    ctx.lineTo(0, lastPoint);
    ctx.lineTo(tickSize, lastPoint);
  }
  ctx.stroke();

  // If active (hover or dragging), draw the highlight line on top
  if (isDefined(selected) && (isDragging || isHovered) && selected) {
    ctx.lineWidth = strokeWidth + 3;
    ctx.strokeStyle = 'rgba(137, 207, 240, 0.5)'; // Blueish with some opacity

    ctx.beginPath();
    if (xAxis) {
      ctx.moveTo(firstPoint, tickSize);
      ctx.lineTo(firstPoint, 0);
      ctx.lineTo(lastPoint, 0);
      ctx.lineTo(lastPoint, tickSize);
    } else {
      ctx.moveTo(tickSize, firstPoint);
      ctx.lineTo(0, firstPoint);
      ctx.lineTo(0, lastPoint);
      ctx.lineTo(tickSize, lastPoint);
    }
    ctx.stroke();
  }
};

const tickHelper = (props: AxisProps, scale: ScaleContinuousNumeric<number, number>) => {
  const {
    orient,
    innerTickSize = 4,
    tickFormat,
    tickPadding = 4,
    tickLabelFill,
    tickStrokeWidth,
    tickStrokeDasharray,
    fontSize = 12,
    fontFamily,
    fontWeight,
    showTicks,
    showTickLabel,
    ticks: tickArguments,
    tickValues: tickValuesProp,
    tickStrokeStyle,
    tickInterval,
    tickIntervalFunction,
    ...rest
  } = props;

  let tickValues: number[];
  if (tickValuesProp !== undefined) {
    if (typeof tickValuesProp === 'function') {
      tickValues = tickValuesProp(scale.domain());
    } else {
      tickValues = tickValuesProp;
    }
  } else if (tickInterval !== undefined) {
    const [min, max] = scale.domain();
    const baseTickValues = d3Range(min, max, (max - min) / tickInterval);

    tickValues = tickIntervalFunction ? tickIntervalFunction(min, max, tickInterval) : baseTickValues;
  } else if (scale.ticks !== undefined) {
    tickValues = scale.ticks(tickArguments);
  } else {
    tickValues = scale.domain();
  }

  const format = tickFormat === undefined ? scale.tickFormat(tickArguments) : (d: number) => tickFormat(d) || '';

  const sign = orient === 'top' || orient === 'left' ? -1 : 1;
  const tickSpacing = Math.max(innerTickSize, 0) + tickPadding;

  let ticks: Tick[];
  let dy;
  // tslint:disable-next-line: variable-name
  let canvas_dy;
  let textAnchor;

  if (orient === 'bottom' || orient === 'top') {
    dy = sign < 0 ? '0em' : '.71em';
    canvas_dy = sign < 0 ? 0 : fontSize * 0.71;
    textAnchor = 'middle';

    const y2 = sign * innerTickSize;
    const labelY = sign * tickSpacing;

    ticks = tickValues.map(d => {
      const x = Math.round(scale(d));
      return {
        value: d,
        x1: x,
        y1: 0,
        x2: x,
        y2,
        labelX: x,
        labelY,
      };
    });

    if (showTicks) {
      const nodes = ticks.map(d => ({ id: d.value, value: d.value, fy: d.y2, origX: d.x1 }));

      const simulation = forceSimulation(nodes)
        .force('x', forceX<ForceNode>(d => d.origX).strength(1))
        .force('collide', forceCollide(22))
        .stop();

      for (let i = 0; i < 100; ++i) {
        simulation.tick();
      }

      // @ts-expect-error
      ticks = zip(ticks, nodes).map(d => {
        const a = d[0];
        // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
        const b = d[1] as unknown as ForceNode;

        if (b.x !== undefined && Math.abs(b.x - b.origX) > 0.01) {
          return {
            ...a,
            x2: b.x,
            labelX: b.x,
          };
        }
        return a;
      });
    }
  } else {
    ticks = tickValues.map(d => {
      const y = Math.round(scale(d));
      const x2 = sign * innerTickSize;
      const labelX = sign * tickSpacing;
      return {
        value: d,
        x1: 0,
        y1: y,
        x2,
        y2: y,
        labelX,
        labelY: y,
      };
    });

    dy = '.32em';
    canvas_dy = fontSize * 0.32;
    textAnchor = sign < 0 ? 'end' : 'start';
  }

  return {
    orient,
    ticks,
    scale,
    tickStrokeStyle,
    tickLabelFill: tickLabelFill || tickStrokeStyle,
    tickStrokeWidth,
    tickStrokeDasharray,
    dy,
    canvas_dy,
    textAnchor,
    fontSize,
    fontFamily,
    fontWeight,
    format,
    showTickLabel,
    ...rest,
  };
};

const drawTicks = (ctx: CanvasRenderingContext2D, result: ReturnType<typeof tickHelper>) => {
  const { ticks, tickStrokeStyle } = result;

  if (tickStrokeStyle !== undefined) {
    ctx.strokeStyle = tickStrokeStyle;
    ctx.fillStyle = tickStrokeStyle;
  }

  ticks.forEach((tick: Tick) => {
    drawEachTick(ctx, tick, result);
  });
};

const drawGridLine = (
  ctx: CanvasRenderingContext2D,
  tick: Tick,
  result: ReturnType<typeof tickHelper>,
  moreProps: MoreProps,
  margin: { left: number; top: number },
) => {
  const { orient, gridLinesStrokeStyle, gridLinesStrokeDasharray } = result;

  const { chartConfig } = moreProps;

  const { height, width, origin } = chartConfig;

  // TradingView 스타일: ratio 1~1.99 → 1px, 2~2.99 → 2px, ...
  const ratio = moreProps.ratio || 1;
  const physicalLineWidth = Math.max(1, Math.floor(ratio));

  // 픽셀 정렬 규칙:
  // - 홀수 픽셀 선(1px, 3px): 픽셀 중앙(정수 + 0.5)에 그려야 선명함
  // - 짝수 픽셀 선(2px, 4px): 정수 좌표에 그려야 선명함
  const isOddWidth = physicalLineWidth % 2 === 1;
  const pixelOffset = isOddWidth ? 0.5 : 0;

  // 세로선(X축): margin.left를 고려하여 전체 물리 좌표 계산 후 정렬
  const alignToPixelX = (coord: number) => {
    const totalCss = coord + margin.left; // 전체 CSS 좌표
    const physicalPixel = Math.floor(totalCss * ratio); // 물리 픽셀 (정수)
    const alignedPhysical = physicalPixel + pixelOffset; // 선 두께에 따라 오프셋 적용
    const alignedCss = alignedPhysical / ratio; // CSS 좌표로 변환
    return alignedCss - margin.left; // 로컬 좌표로 변환
  };

  // 가로선(Y축): margin.top + origin[1]을 고려
  const alignToPixelY = (coord: number) => {
    const totalCss = coord + margin.top + origin[1];
    const physicalPixel = Math.floor(totalCss * ratio);
    const alignedPhysical = physicalPixel + pixelOffset; // 선 두께에 따라 오프셋 적용
    const alignedCss = alignedPhysical / ratio;
    return alignedCss - margin.top - origin[1];
  };

  if (gridLinesStrokeStyle !== undefined) {
    ctx.strokeStyle = gridLinesStrokeStyle;
  }

  // Anti-aliasing 비활성화 시도
  ctx.imageSmoothingEnabled = false;

  ctx.beginPath();

  const sign = orient === 'top' || orient === 'left' ? 1 : -1;

  switch (orient) {
    case 'top':
    case 'bottom': {
      const alignedX = alignToPixelX(tick.x1);
      ctx.moveTo(alignedX, 0);
      ctx.lineTo(alignedX, sign * height);
      break;
    }
    default: {
      const alignedY = alignToPixelY(tick.y1);
      ctx.moveTo(0, alignedY);
      ctx.lineTo(sign * width, alignedY);
      break;
    }
  }
  // 선 두께도 물리 픽셀 기준으로 설정
  ctx.lineWidth = physicalLineWidth / ratio;

  const lineDash = getStrokeDasharrayCanvas(gridLinesStrokeDasharray);

  ctx.setLineDash(lineDash);
  ctx.stroke();
};

const drawEachTick = (ctx: CanvasRenderingContext2D, tick: Tick, result: ReturnType<typeof tickHelper>) => {
  const { tickStrokeWidth, tickStrokeDasharray } = result;

  ctx.beginPath();

  ctx.moveTo(tick.x1, tick.y1);
  ctx.lineTo(tick.x2, tick.y2);
  ctx.lineWidth = tickStrokeWidth ?? 1;

  const lineDash = getStrokeDasharrayCanvas(tickStrokeDasharray);

  ctx.setLineDash(lineDash);
  ctx.stroke();
};

const drawEachTickLabel = (ctx: CanvasRenderingContext2D, tick: Tick, result: ReturnType<typeof tickHelper>) => {
  const { canvas_dy, format } = result;

  const text = format(tick.value);

  ctx.beginPath();

  ctx.fillText(text, tick.labelX, tick.labelY + canvas_dy);
};
